<template>
	<md-menu @md-opened="onMenuOpen" ref="menu">
		<md-field :class="[{ 'md-error': error }]">
			<label>{{ labelText }}</label>
			<!-- TODO: Add keyboard support-->
			<md-input :value="selectedValuesDisplay"
					  :disabled="disabled"
					  :required="required"
					  readonly
					  @click="toggleMenu"
					  class="pointer"
			/>
			<!-- Matches style of select dropdown icon -->
			<div @click="toggleMenu">
				<md-icon class="md-icon-image pointer" v-once>
					<svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
						<path d="M7 10l5 5 5-5z"/>
						<path d="M0 0h24v24H0z" fill="none"/>
					</svg>
				</md-icon>
			</div>
			<span v-if="error" class="validation-error">{{ error }}</span>
		</md-field>

		<keep-alive>
			<md-menu-content class="tree-select-menu tree-select-menu-dense" :class="contentClass" :style="contentStyle">
				<md-menu-item>
					<md-field v-if="searchAllow">
						<label>{{ $t("common.search") }}</label>
						<md-input @input="search" :value="searchValue"></md-input>
					</md-field>
				</md-menu-item>

				<md-menu-item>
					<el-tree :data="options"
							 :props="treeProperties"
							 :node-key="optionsKeyField"
							 @check="onNodeChecked"
							 :filter-node-method="filterTreeNode"
							 :show-checkbox="true"
							 :highlight-current="true"
							 :empty-text="emptyText"
							 ref="tree"
					></el-tree>
				</md-menu-item>
			</md-menu-content>
		</keep-alive>
	</md-menu>
</template>

<script>
import SelectBaseComponent from "@/components/templates/common/SelectBaseComponent";

export default {
	extends: SelectBaseComponent,
	props: {
		value: { type: Array, default: [] },
		labelText: { type: String, default: "" },
		selectedValuesTextMultiple: { type: String, default: "" }, // Manually set text for multiple selected options
		selectedValuesTextSingle: { type: String, default: "" }, // Manually set text for multiple selected options
		optionChildrenField: { type: String, default: "children" },
		contentSize: { type: "small" | "medium" | "large" | "xl" | "auto", default: "medium" },
		autoMaxWidth: { type: Number, default: "100%" }, // Only applicable if content size is "auto"
		autoMaxHeight: { type: Number, default: "50%" }, // Only applicable if content size is "auto"
		error: { type: String, default: "" },
	},
	data() {
		return {
			selectedValuesDisplay: ""
		}
	},
	created() {
		if (this.optionsKeyField && (this.optionsKeyField !== this.optionsValueField)) {
			console.error("tree select needs option keys and values to be equal");
		}

		if (this.optionsKeyGetter || this.optionsValueGetter) {
			console.error("tree select does not support key or value getters yet");
		}

		// make sure keys field is set equal to values
		this.optionsKeyField = this.optionsValueField;

		// Make sure to use the provided tree filtering instead
		this.searchCustom = true;

		this.syncOnValueUpdate();
	},
	computed: {
		contentClass() {
			return "tree-select-menu-content-" + this.contentSize;
		},
		contentStyle() {
			if (this.contentSize !== "auto") {
				return "";
			}

			let maxWidthStyle = "maxWidth: " + this.autoMaxWidth + "!important;";
			let maxHeightStyle = "maxHeight: " + this.autoMaxHeight + "!important;";

			return maxWidthStyle + maxHeightStyle;
		},
		treeProperties() {
			return {
				children: this.optionChildrenField,
				label: this.optionsDisplayField || this.getOptionDisplayValue
			}
		},
	},
	methods: {
		toggleMenu() {
			this.$refs.menu.toggleContent();
		},
		onMenuOpen() {
			// Need to wait for tree to be rendered
			this.$nextTick(() => {
				this.syncTreeState();
			});
		},
		filterTreeNode(_, data) {
			return !this.searchValue || data.label.includes(this.searchValue);
		},
		onNodeChecked() {
			this.value = this.$refs.tree.getCheckedNodes(true).map(this.getOptionValue);

			this.$emit("input", this.value); // To make v-model work
			this.$emit("md-selected", this.value); // to match normal select event
		},
		updateSelectedDisplay() {
			if (this.value.length === 0) {
				this.selectedValuesDisplay = "";
			} else if (this.value.length === 1 && this.selectedValuesTextSingle) {
				this.selectedValuesDisplay = this.selectedValuesTextSingle
			} else if (this.value.length > 1 && this.selectedValuesTextMultiple) {
				this.selectedValuesDisplay = this.selectedValuesTextMultiple
			} else {
				// Cannot use the checked tree nodes because it might not be rendered or still out of sync if the selected values changed from the outside
				// So we need to look up the options data by searching through the data itself
				this.selectedValuesDisplay = this.findOptionsForSelectedValues().map(this.getOptionDisplayValue).join(", ");
			}
		},
		applyFilter() {
			this.$refs.tree.filter();
		},
		syncOnValueUpdate() {
			if (this.isTreeRendered()) {
				this.syncTreeState();
			}

			this.updateSelectedDisplay();
		},
		syncTreeState() {
			this.applyFilter();
			this.$refs.tree.setCheckedKeys(this.value);
		},
		isTreeRendered() {
			return !!this.$refs.tree;
		},
		findOptionsForSelectedValues() {
			// No options, so nothing to look for
			if (!this.options) {
				return [];
			}

			let stack = [...this.options]
			let result = [];

			while (stack.length > 0 && result.length < this.value.length) {
				const option = stack.shift();

				if (this.value.includes(this.getOptionValue(option))) {
					result.push(option);
				}

				stack.unshift(...option[this.optionChildrenField]);
			}

			return result;
		}
	},
	watch: {
		searchValue() {
			if (this.isTreeRendered()) {
				this.applyFilter();
			}
		},
		value() {
			this.syncOnValueUpdate();
		},
		options() {
			this.syncOnValueUpdate();
		}
	}
};
</script>